import { HeaderIcons, AppIcon, getGameIconName, RankMedal } from './icons';
import React, { useState } from 'react';
import { useTranslation } from '../../i18n';
import { appVariant } from '../../config';
import Header from './Header';
import RoundFeedbackPrompt from './RoundFeedbackPrompt';
import MenuPageWrapper from '../menu/MenuPageWrapper';
import PremiumPage from '../menu/PremiumPage';
import '../../App.css';
import './GameEndedScreen.css';

interface GameEndedScreenProps {
  /** Game id used to resolve the header AppIcon. */
  gameType?: string;
  finalLeaderboard: Array<{ playerId: string; playerName: string; score: number }>;
  myPlayerId: string;
  /** When set, leaderboard rows are teams (playerId = 'A' | 'B') and highlight/score use this team. */
  myTeamId?: 'A' | 'B';
  myScore: number;
  isHost: boolean;
  onGoHome: () => void;
  onPlayAgain: () => void;
  onPlayAnotherGame?: () => void;
  onGetPremium?: () => void;
  /** Show a highlighted position box at the top (SpeedChallenge style) */
  showPositionHighlight?: boolean;
  /** Add "(You)" suffix to player name in leaderboard */
  showYouSuffix?: boolean;
  /** Game statistics to display */
  statistics?: {
    mostCorrectAnswers?: { playerId: string; playerName: string; count: number };
    wrongAnswers: Array<{ playerId: string; playerName: string; answer: string; questionId: string; questionText?: string }>;
    totalQuestions: number;
    averageScore: number;
  };
  feedbackConfig?: {
    roomName: string;
    gameType: string;
    socket: {
      emit: (event: string, data: any) => void;
    };
  };
}

const getPackGameType = (): 'blitz' | 'team' | 'family' | 'party' => {
  const variantMap: Record<string, 'blitz' | 'team' | 'family' | 'party'> = {
      blitz: 'blitz',
      team: 'team',
      family: 'family',
      party: 'party',
      coop: 'blitz',
    };
  return variantMap[appVariant] || 'blitz';
};

const GameEndedScreen: React.FC<GameEndedScreenProps> = ({
  gameType,
  finalLeaderboard,
  myPlayerId,
  myTeamId,
  myScore,
  isHost,
  onGoHome,
  onPlayAgain,
  onPlayAnotherGame,
  onGetPremium,
  showPositionHighlight = false,
  showYouSuffix = false,
  statistics,
  feedbackConfig,
}) => {
  const { t } = useTranslation();
  const [showPremiumPage, setShowPremiumPage] = useState(false);

  const handleGetPremium = onGetPremium ?? (() => setShowPremiumPage(true));
  const headerIconName = gameType ? getGameIconName(gameType) : 'trophy';

  if (showPremiumPage) {
    const packGameType = getPackGameType();
    return (
      <MenuPageWrapper
        title={t('common.premium')}
        onMenuClick={() => {}}
        onBack={() => setShowPremiumPage(false)}
        gameType={packGameType}
      >
        <PremiumPage gameType={packGameType} />
      </MenuPageWrapper>
    );
  }

  const isTeamMode = !!myTeamId;
  const myEntryId = isTeamMode ? myTeamId : myPlayerId;
  const myEntryData = finalLeaderboard.find((p) => p.playerId === myEntryId);
  const finalScore = myEntryData?.score ?? (isTeamMode ? 0 : myScore);
  const myPosition = finalLeaderboard.findIndex((p) => p.playerId === myEntryId) + 1;
  const totalPlayers = finalLeaderboard.length;

  // Get a random "crazy" wrong answer for display
  const getCraziestWrongAnswer = () => {
    if (!statistics || statistics.wrongAnswers.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * statistics.wrongAnswers.length);
    return statistics.wrongAnswers[randomIndex];
  };

  const craziestWrongAnswer = getCraziestWrongAnswer();

  return (
    <div className={`app${!isHost ? ' game-ended-screen--player' : ''}`}>
      <Header
        leftButton={{ icon: HeaderIcons.home, onClick: onGoHome, tooltip: t('common.tooltips.home') }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage')
        }}
        centerContent={
          <span className="game-ended-title">
            <AppIcon name={headerIconName} fontSize="small" aria-hidden />
            {t('gameplay.gameEnd.gameFinished')}
          </span>
        }
      />
      <div className="container player-content-wrapper" style={{ paddingTop: '0.5rem' }}>
        {!isHost && (
          <div
            style={{
              marginBottom: '0.85rem',
              padding: '0.8rem 1rem',
              textAlign: 'center',
              color: '#111827',
              fontSize: '1rem',
              fontWeight: 600,
            }}
          >
            {t('gameplay.gameEnd.waitingForHost')}
          </div>
        )}
        {(!showPositionHighlight || myPosition <= 0) && (
          <div style={{
            textAlign: 'center',
            marginBottom: '1rem',
            fontSize: '1.15rem',
            color: '#111827',
            fontWeight: 'bold',
          }}>
            {isTeamMode
              ? t('gameplay.gameEnd.yourTeamScore', { score: finalScore })
              : t('gameplay.gameEnd.yourScore', { score: finalScore })}
          </div>
        )}
        
        {/* Leaderboard at the top */}
        <div className="leaderboard" style={{ marginBottom: statistics ? '0.75rem' : '0' }}>
          {finalLeaderboard.map((player, index) => {
            const isMe = isTeamMode
              ? player.playerId === myTeamId
              : player.playerId === myPlayerId;
            return (
              <div
                key={player.playerId}
                className="leaderboard-item"
                style={isMe ? {
                  backgroundColor: 'rgba(var(--color-primary-rgb), 0.3)',
                  border: '2px solid var(--color-primary)',
                } : {}}
              >
                <span
                  className="game-ended-rank-name"
                  style={isMe ? { color: 'var(--color-primary)', fontWeight: 'bold' } : {}}
                >
                  {index < 3 ? <RankMedal index={index} fontSize="small" /> : null}
                  {player.playerName}
                  {isMe && showYouSuffix && ` ${t('common.you')}`}
                </span>
                <span style={isMe ? { color: 'var(--color-primary)', fontWeight: 'bold' } : {}}>
                  <strong>{player.score}</strong>{!showPositionHighlight && ` ${t('common.points')}`}
                </span>
              </div>
            );
          })}
        </div>

        {!isHost && (
          <button
            type="button"
            data-testid="get-premium-button"
            className="game-ended-get-premium-btn"
            onClick={handleGetPremium}
          >
            <AppIcon name="premium" fontSize="small" aria-hidden />
            {t('gameplay.gameEnd.getPremium')}
          </button>
        )}

        {/* Statistics Section */}
        {statistics && (
          <div style={{
            marginBottom: '1rem',
            padding: '1rem',
            backgroundColor: 'rgba(255, 255, 255, 0.92)',
            borderRadius: '14px',
            border: '1.5px solid rgba(var(--color-secondary-rgb), 0.2)',
          }}>
            <h2 className="game-ended-stats-heading">
              <AppIcon name="barChart" fontSize="small" aria-hidden />
              {t('gameplay.gameEnd.gameStatistics')}
            </h2>
            
            <div className="game-ended-stats-grid">
              {statistics.mostCorrectAnswers && statistics.mostCorrectAnswers.count > 0 && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(var(--color-primary-rgb), 0.2)',
                  borderRadius: '10px',
                  border: '1.5px solid var(--color-primary)',
                }}>
                  <div className="game-ended-stat-label">
                    <AppIcon name="trackChanges" fontSize="inherit" aria-hidden />
                    {t('gameplay.gameEnd.mostCorrectAnswers')}
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#111827' }}>
                    {statistics.mostCorrectAnswers.playerName}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--color-primary)' }}>
                    {statistics.mostCorrectAnswers.count} {t('common.correct')}
                  </div>
                </div>
              )}

              {statistics.averageScore > 0 && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(52, 211, 153, 0.2)',
                  borderRadius: '10px',
                  border: '1.5px solid #34D399',
                }}>
                  <div className="game-ended-stat-label">
                    <AppIcon name="trendingUp" fontSize="inherit" aria-hidden />
                    {t('gameplay.gameEnd.averageScore')}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#059669' }}>
                    {statistics.averageScore}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#4B5563' }}>
                    {t('common.points')}
                  </div>
                </div>
              )}

              {statistics.totalQuestions > 0 && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(245, 158, 11, 0.2)',
                  borderRadius: '10px',
                  border: '1.5px solid #F59E0B',
                }}>
                  <div className="game-ended-stat-label">
                    <AppIcon name="quiz" fontSize="inherit" aria-hidden />
                    {t('gameplay.gameEnd.totalQuestions')}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#D97706' }}>
                    {statistics.totalQuestions}
                  </div>
                </div>
              )}

              {totalPlayers > 0 && (
                <div style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(59, 130, 246, 0.14)',
                  borderRadius: '10px',
                  border: '1.5px solid #3B82F6',
                }}>
                  <div className="game-ended-stat-label">
                    <AppIcon name="people" fontSize="inherit" aria-hidden />
                    {t('gameplay.gameEnd.totalPlayers')}
                  </div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#2563EB' }}>
                    {totalPlayers}
                  </div>
                </div>
              )}

              {craziestWrongAnswer && (
                <div style={{
                  padding: '0.85rem',
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  borderRadius: '10px',
                  border: '1.5px solid #EF4444',
                  gridColumn: '1 / -1', // Span full width
                }}>
                  <div className="game-ended-stat-label">
                    <AppIcon name="sentimentVerySatisfied" fontSize="inherit" aria-hidden />
                    {t('gameplay.gameEnd.craziestWrongAnswer')}
                  </div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.2rem' }}>
                    "{craziestWrongAnswer.answer}"
                  </div>
                  {craziestWrongAnswer.questionText && (
                    <div style={{ fontSize: '0.8rem', color: '#374151', marginBottom: '0.2rem' }}>
                      {t('gameplay.gameEnd.questionLabel', { text: craziestWrongAnswer.questionText })}
                    </div>
                  )}
                  <div style={{ fontSize: '0.78rem', color: '#4B5563' }}>
                    {t('gameplay.gameEnd.byPlayer', { name: craziestWrongAnswer.playerName })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {isHost ? (
          <div className="game-finished-buttons" style={{ 
            marginTop: '2rem', 
            display: 'flex', 
            gap: '0.5rem', 
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <button 
              className="button game-ended-action-btn" 
              onClick={onPlayAgain} 
              style={{ 
                width: '50%',
                padding: '0.5rem 1rem !important',
                fontSize: '0.85rem !important',
                minWidth: '200px',
                maxWidth: '400px',
                minHeight: 'auto !important',
                height: 'auto !important',
              }}
            >
              <AppIcon name="celebration" fontSize="small" aria-hidden />
              {t('gameplay.gameEnd.playAgain')}
            </button>
            {onPlayAnotherGame && (
              <button 
                className="button game-ended-action-btn" 
                onClick={onPlayAnotherGame} 
                style={{ 
                  width: '50%',
                  padding: '0.5rem 1rem !important',
                  fontSize: '0.85rem !important',
                  backgroundColor: 'var(--color-primary)',
                  minWidth: '200px',
                  maxWidth: '400px',
                  minHeight: 'auto !important',
                  height: 'auto !important',
                }}
              >
                <AppIcon name="gamepad" fontSize="small" aria-hidden />
                {t('gameplay.gameEnd.playAnotherGame')}
              </button>
            )}
          </div>
        ) : null}

        {!isHost && feedbackConfig && (
          <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <RoundFeedbackPrompt
              roundId={`game-ended-${feedbackConfig.roomName}-${feedbackConfig.gameType}`}
              onSubmitFeedback={(message) => {
                feedbackConfig.socket.emit('round-feedback', {
                  roomName: feedbackConfig.roomName,
                  gameType: feedbackConfig.gameType,
                  roundId: `game-ended-${feedbackConfig.roomName}-${feedbackConfig.gameType}`,
                  message,
                });
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GameEndedScreen;
